import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Keyboard } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';
import { ROUTE_TO_PAGE, can } from '@/lib/permissions';

// Global keyboard shortcuts. Mounted once inside <Layout/>.
//
// Bindings: Ctrl/Cmd + Shift + <letter>. The help dialog opens via:
//   - `?` (Shift+/), `Ctrl+/`
//   - Programmatic event: window.dispatchEvent(new Event('open-keyboard-shortcuts'))
//     (used by the Settings page button — there is no longer a floating trigger)
const SHORTCUTS = [
  { key: 'N', label: 'New Invoice/Token',       to: '/invoices/create', accent: 'text-[#4F46E5]', sourceRoute: '/invoices' },
  { key: 'T', label: 'Transfer',       to: '/transfer',        accent: 'text-[#F59E0B]' },
  { key: 'I', label: 'Income',         to: '/income',          accent: 'text-[#10B981]' },
  { key: 'E', label: 'Payment',        to: '/expense',         accent: 'text-[#EF4444]' },
  { key: 'D', label: 'Dashboard',      to: '/dashboard',       accent: 'text-[#2563EB]' },
  { key: 'R', label: 'Reports',        to: '/reports',         accent: 'text-[#0D9488]' },
  { key: 'F', label: 'Follow-Up',      to: '/processing',      accent: 'text-[#D97706]' },
  { key: 'O', label: 'To-Do Notes',    to: '/notes',           accent: 'text-[#7C3AED]' },
  { key: 'C', label: 'Customer',       to: '/customers',       accent: 'text-[#DB2777]' },
  { key: 'S', label: 'Services',       to: '/services',        accent: 'text-[#7C3AED]' },
];

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');
const MOD_LABEL = isMac ? '\u2318' : 'Ctrl';
const SHIFT_LABEL = isMac ? '\u21E7' : 'Shift';
const COMBO_PREFIX = isMac ? `${MOD_LABEL}${SHIFT_LABEL}` : `${MOD_LABEL}+${SHIFT_LABEL}+`;

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  if (el.closest && el.closest('[cmdk-root]')) return true;
  return false;
};

/** Settings-page button. Dispatches a window event picked up by <KeyboardShortcuts/>. */
export function KeyboardShortcutsButton({ className = '' }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => window.dispatchEvent(new Event('open-keyboard-shortcuts'))}
      className={`rounded-lg border-[#E4E4E7] hover:bg-[#F4F4F5] ${className}`}
      data-testid="open-keyboard-shortcuts-btn"
    >
      <Keyboard size={14} className="mr-1.5 text-[#3B82F6]" weight="duotone" />
      View Keyboard Shortcuts
    </Button>
  );
}

export default function KeyboardShortcuts() {
  const navigate = useNavigate();
  const { user, permissions } = useAuth();
  const [open, setOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const allowedShortcuts = isAdmin ? SHORTCUTS : SHORTCUTS.filter(s => {
    const route = s.sourceRoute || s.to;
    const pageKey = ROUTE_TO_PAGE[route];
    if (!pageKey) return true;
    return can(permissions, pageKey, 'view');
  });

  // External trigger from the Settings button.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('open-keyboard-shortcuts', onOpen);
    return () => window.removeEventListener('open-keyboard-shortcuts', onOpen);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (isTypingTarget(e.target)) return;

      const isHelpKey = e.key === '?' || (e.key === '/' && e.shiftKey) || (e.key === '/' && (e.ctrlKey || e.metaKey));
      if (isHelpKey) {
        e.preventDefault();
        setOpen(o => !o);
        return;
      }

      const wantsModifier = (e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey;
      if (!wantsModifier) return;

      const k = (e.key || '').toUpperCase();
      const match = allowedShortcuts.find(s => s.key === k);
      if (match) {
        e.preventDefault();
        setOpen(false);
        navigate(match.to);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, allowedShortcuts]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-md" data-testid="kbd-shortcuts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard size={20} className="text-[#3B82F6]" weight="duotone" /> Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Combine <kbd className="px-1.5 py-0.5 rounded bg-[#F4F4F5] border border-[#E4E4E7] text-[10px] font-mono">{MOD_LABEL}</kbd>
            {' + '}
            <kbd className="px-1.5 py-0.5 rounded bg-[#F4F4F5] border border-[#E4E4E7] text-[10px] font-mono">{SHIFT_LABEL}</kbd>
            {' + '}letter to jump anywhere. Press
            {' '}
            <kbd className="px-1.5 py-0.5 rounded bg-[#F4F4F5] border border-[#E4E4E7] text-[10px] font-mono">?</kbd>
            {' '}any time to reopen this list.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2" data-testid="kbd-shortcuts-list">
          {allowedShortcuts.map(s => (
            <div
              key={s.key}
              className="flex items-center justify-between px-3 py-2 rounded-lg border border-[#E4E4E7] hover:border-[#A1A1AA] hover:bg-[#FAFAFA] transition-colors"
            >
              <span className={`text-sm font-medium ${s.accent}`}>{s.label}</span>
              <kbd className="px-2 py-0.5 rounded-md bg-[#0A0A0A] text-white text-[11px] font-mono font-bold shadow-sm whitespace-nowrap">
                {COMBO_PREFIX}{s.key}
              </kbd>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#A1A1AA] text-center pt-1">
          Shortcuts are disabled while typing in any input field.
        </p>
      </DialogContent>
    </Dialog>
  );
}
